import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  OnDestroy,
  AfterViewInit,
  signal,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  CalendarDateFormatter,
  CalendarEvent,
  CalendarEventTimesChangedEvent,
  CalendarView,
  CalendarPreviousViewDirective,
  CalendarNextViewDirective,
  CalendarMonthViewComponent,
  CalendarWeekViewComponent,
  CalendarDayViewComponent,
  CalendarTodayDirective,
} from 'angular-calendar';
import {
  isSameMonth,
  isSameDay,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  startOfDay,
  endOfDay,
} from 'date-fns';
import { BehaviorSubject, filter, Subscription } from 'rxjs';
import { RideDetailsComponent } from './dialogs/ride-details/ride-details.component';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { EditRideComponent } from './dialogs/edit-ride/edit-ride.component';
import { EditAutomaticRideComponent } from './dialogs/edit-automatic-ride/edit-automatic-ride.component';
import { CustomDateFormatter } from './custom-date-formatter.provider';
import { getDateString, redirectIfDriver, valueToDate, exportJsonToExcel } from '../globals';
import { Daypart, DaypartDay, Vehicle, User, Ride, VehicleGroup } from '../interfaces';
import { PlanDaypartComponent } from './dialogs/plan-daypart/plan-daypart.component';
import { NavigationEnd, Router } from '@angular/router';
import { DatePipe, NgStyle, NgClass, NgTemplateOutlet, AsyncPipe } from '@angular/common';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  Unsubscribe,
  updateDoc,
  where,
} from 'firebase/firestore';
import moment from 'moment';
import { db, dataStore } from '../app.component';
import { getWeekString, updateRideToV2 } from './helpers/ride-helper';
import { Role, PlanType } from '../enums';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatButton, MatIconButton, MatFabButton } from '@angular/material/button';
import { MatInput } from '@angular/material/input';
import { MatDatepicker, MatDatepickerInput } from '@angular/material/datepicker';
import { PlannerListViewComponent } from './components/list-view/list-view.component';
import { AutoPlanComponent } from './components/auto-plan/auto-plan.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTooltip } from '@angular/material/tooltip';
import { PermissionsService } from '../services/permissions.service';

export enum ViewMode {
  Month = 'month',
  Week = 'week',
  Day = 'day',
  List = 'list',
}

@Component({
  selector: 'app-planner',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './planner.component.html',
  styleUrls: ['./planner.component.scss'],
  providers: [
    {
      provide: CalendarDateFormatter,
      useClass: CustomDateFormatter,
    },
    DatePipe,
  ],
  imports: [
    NgStyle,
    MatIcon,
    MatFormField,
    MatSelect,
    FormsModule,
    ReactiveFormsModule,
    NgClass,
    MatOption,
    MatButton,
    NgTemplateOutlet,
    MatLabel,
    MatInput,
    MatIconButton,
    MatSuffix,
    CalendarPreviousViewDirective,
    MatDatepicker,
    MatDatepickerInput,
    CalendarNextViewDirective,
    CalendarMonthViewComponent,
    CalendarWeekViewComponent,
    CalendarDayViewComponent,
    PlannerListViewComponent,
    AutoPlanComponent,
    MatProgressSpinner,
    MatFabButton,
    MatTooltip,
    CalendarTodayDirective,
    AsyncPipe,
    DatePipe,
  ],
})
export class PlannerComponent implements OnInit, OnDestroy, AfterViewInit {
  dataStore = dataStore;
  PlanType = PlanType;
  CalendarView = CalendarView;
  ViewMode = ViewMode;

  calendarView = signal<CalendarView>(CalendarView.Week);
  view = signal<ViewMode>(ViewMode.Week);
  planType = signal<PlanType>(PlanType.Rides);

  calendarStartHour = signal(9);
  calendarEndHour = signal(21);
  weekdays = ['zondag', 'maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag'];
  minimized = signal('out');
  allowPlannerScheduling = signal(true);

  currentDate = signal<Date>(moment().startOf('day').toDate());
  viewDate = signal<Date>(moment().startOf('day').toDate());
  viewBeginDate = signal<Date>(new Date());
  viewEndDate = signal<Date>(new Date());
  weekPerMonth = signal<Array<number>>([]);

  ridesSnapshot: Unsubscribe;
  daypartDaysDriverSnapshot: Unsubscribe;
  daypartDaysPlannerSnapshot: Unsubscribe;

  public filteredEvents$ = new BehaviorSubject<any>(null);
  public filteredVehicles$ = new BehaviorSubject<Vehicle[]>([]);
  public ridesPerVehicle$ = new BehaviorSubject<Map<string, Array<CalendarEvent>>>(new Map());

  allDaypartDaysDriverEvents = signal<CalendarEvent<any>[]>([]);
  allDaypartDaysPlannerEvents = signal<CalendarEvent<any>[]>([]);
  allRideEvents = signal<CalendarEvent<any>[]>([]);

  vehicles = signal<Vehicle[]>([]);
  vehicleGroups = signal<VehicleGroup[]>([]);
  drivers = signal<User[]>([]);
  dayPartsDriver = signal<Daypart[]>([]);
  dayPartsPlanner = signal<Daypart[]>([]);

  activeDayIsOpen = false;
  organisationId = dataStore.selectedOrganisation.id;
  rideDuration = signal<number>(15);
  hourSegments = signal<number>(4);
  msPerSegment = signal<number>(15 * 60000);

  ignoreDriverStatus = signal(false);
  refreshingRides = signal(false);
  exporting = signal(false);
  showDriverPlanning = signal(dataStore.paidFunctionalities.usesDriverPlanning ?? false);
  showPlannerPlanning = signal(
    (dataStore.paidFunctionalities.usesDriverPlanning ?? false) &&
      (dataStore.generalSettings.usesPlannerPlanning ?? false),
  );
  showAutomaticPlanning = signal(dataStore.paidFunctionalities.usesAutomaticPlanning ?? false);

  filterForm = this.fb.group({
    search: [''],
    planType: [PlanType.Rides],
    vehicle: ['all'],
    vehicleGroup: ['all'],
  });

  private routeSubscription: Subscription = new Subscription();

  constructor(
    private fb: FormBuilder,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private router: Router,
    private permissionsService: PermissionsService,
  ) {}

  ngOnInit(): void {
    const roles = dataStore.orgUser.roles || [];
    if (this.permissionsService.hasAtLeastRole(roles, Role.Planner)) {
      this.allowPlannerScheduling.set(true);
    }

    if (dataStore.generalSettings) {
      this.rideDuration.set(
        dataStore.generalSettings.customRideDuration
          ? dataStore.generalSettings.customRideDuration
          : 15,
      );
      this.ignoreDriverStatus.set(
        dataStore.generalSettings.ignoreDriverStatus
          ? dataStore.generalSettings.ignoreDriverStatus
          : false,
      );
      if (dataStore.generalSettings.daypartSettings) {
        if (dataStore.generalSettings.daypartSettings.startHour) {
          this.calendarStartHour.set(dataStore.generalSettings.daypartSettings.startHour);
        }
        if (dataStore.generalSettings.daypartSettings.endHour) {
          this.calendarEndHour.set(dataStore.generalSettings.daypartSettings.endHour);
        }
      }
      const segments = dataStore.generalSettings.customSegment
        ? 60 / dataStore.generalSettings.customSegment
        : 60 / 15;
      this.hourSegments.set(segments);
      this.msPerSegment.set((60 / segments) * 60000);

      this.showPlannerPlanning.set(
        this.showDriverPlanning() && (dataStore.generalSettings.usesPlannerPlanning ?? false),
      );
    }
  }

  async ngAfterViewInit(): Promise<void> {
    const storedView = dataStore.orgUser.viewType as ViewMode | undefined;
    const viewToSet =
      storedView && Object.values(ViewMode).includes(storedView) ? storedView : ViewMode.Week;
    await this.setViewMode(viewToSet, false);

    void this.getData()
      .then(() => {
        this.fetchEvents(true);
      })
      .catch((error) => {
        console.error('Error fetching data:', error);
        this.filteredEvents$.next([]);
      });

    if (
      !this.showDriverPlanning() &&
      !this.showPlannerPlanning() &&
      !this.showAutomaticPlanning()
    ) {
      this.filterForm.controls.planType.disable();
    }
    this.filterForm.valueChanges.subscribe(() => {
      if (this.filterForm.value.planType !== PlanType.Automatic) {
        this.filterEvents();
      }
    });

    this.routeSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.updatePlanType();
      });
    this.updatePlanType();
  }

  ngOnDestroy(): void {
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe();
    }
  }

  filterEvents() {
    try {
      let events = [];
      if (this.planType() === PlanType.Rides) {
        events = [...this.allRideEvents()];
      } else if (this.planType() === PlanType.Drivers) {
        events = [...this.allDaypartDaysDriverEvents()];
      } else if (this.planType() === PlanType.Planners) {
        events = [...this.allDaypartDaysPlannerEvents()];
      }

      const searchValue = (this.filterForm.value.search ?? '').toLowerCase();

      const selectedVehicleGroup = this.vehicleGroups().find((vehicleGroup) => {
        return vehicleGroup.id === this.filterForm.value.vehicleGroup;
      });
      events = events
        .filter((event) => {
          let searchPassed = false;
          let vehicleFilterPassed = false;
          let vehicleGroupFilterPassed = false;
          let dateCheckpassed = false;
          if (event.title && event.title.toLowerCase().includes(searchValue)) {
            searchPassed = true;
          }
          const driverName = this.drivers().find((driver) => {
            return driver.id === event.meta?.ride?.driverId;
          });
          if (driverName && driverName.name.toLowerCase().includes(searchValue)) {
            searchPassed = true;
          }

          if (this.planType() !== PlanType.Rides) {
            vehicleFilterPassed = true;
          } else if (this.filterForm.value.vehicle === 'all') {
            vehicleFilterPassed = true;
          } else if (
            this.planType() === PlanType.Rides &&
            event.meta.vehicle &&
            event.meta.vehicle.id === this.filterForm.value.vehicle
          ) {
            vehicleFilterPassed = true;
          }

          if (this.planType() !== PlanType.Rides) {
            vehicleGroupFilterPassed = true;
          } else if (this.filterForm.value.vehicleGroup === 'all') {
            vehicleGroupFilterPassed = true;
          } else if (
            this.planType() === PlanType.Rides &&
            event.meta.vehicle &&
            selectedVehicleGroup?.vehicles?.includes(event.meta.vehicle.id)
          ) {
            vehicleGroupFilterPassed = true;
          }

          if (
            moment(event.start).isSameOrAfter(moment(this.viewBeginDate())) &&
            moment(event.end).isSameOrBefore(moment(this.viewEndDate()))
          ) {
            dateCheckpassed = true;
          }

          if (searchPassed && vehicleFilterPassed && vehicleGroupFilterPassed && dateCheckpassed) {
            return event;
          }
        })
        .sort((a, b) => a.start.getTime() - b.start.getTime());
      if (this.planType() === PlanType.Rides) {
        events.sort((a, b) => {
          if (
            a.start.getTime() === b.start.getTime() &&
            a.meta.vehicle &&
            a.meta.vehicle.calendarStyle &&
            a.meta.vehicle.calendarStyle.sortingNumber &&
            b.meta.vehicle &&
            b.meta.vehicle.calendarStyle &&
            b.meta.vehicle.calendarStyle.sortingNumber
          ) {
            if (
              a.meta.vehicle.calendarStyle.sortingNumber >
              b.meta.vehicle.calendarStyle.sortingNumber
            ) {
              return 1;
            } else {
              return -1;
            }
          }
          return 0;
        });
      }

      if (this.planType() === PlanType.Rides && this.vehicles().length > 0) {
        const ridesPerVehicleMap: Map<string, Array<CalendarEvent>> = new Map();
        for (const event of events) {
          if (event.meta.ride.vehicleId) {
            if (!ridesPerVehicleMap.has(event.meta.ride.vehicleId)) {
              ridesPerVehicleMap.set(event.meta.ride.vehicleId, []);
            }
            const array = ridesPerVehicleMap.get(event.meta.ride.vehicleId);
            array.push(event);
            ridesPerVehicleMap.set(event.meta.ride.vehicleId, array);
          }
        }
        this.ridesPerVehicle$.next(ridesPerVehicleMap);
      }

      const filteredVehicles = [];
      for (const vehicle of this.vehicles()) {
        if (this.filterForm.value.vehicleGroup === 'all') {
          filteredVehicles.push(vehicle);
        } else if (selectedVehicleGroup?.vehicles?.includes(vehicle.id)) {
          filteredVehicles.push(vehicle);
        }
      }
      this.filteredVehicles$.next(filteredVehicles);

      this.filteredEvents$.next(events);
    } catch (error) {
      console.error('filterEvents failed:', error);
      this.filteredEvents$.next([]);
    }
  }

  async fetchEvents(fetchAll: boolean = false) {
    this.filteredEvents$.next(null);
    const view = this.view() === ViewMode.List ? CalendarView.Day : this.calendarView();
    const getStart: any = {
      month: startOfMonth,
      week: startOfWeek,
      day: startOfDay,
    }[view];
    const getEnd: any = {
      month: endOfMonth,
      week: endOfWeek,
      day: endOfDay,
    }[view];

    this.viewBeginDate.set(getStart(this.viewDate(), { weekStartsOn: 1 }));
    this.viewEndDate.set(getEnd(this.viewDate(), { weekStartsOn: 1 }));
    const weeks: number[] = [];
    const beginDateView = moment(this.viewBeginDate()).startOf('isoWeek').toDate();
    for (let i = 0; i < 6; i++) {
      const weeklyMoment = moment(beginDateView).add(i, 'weeks');
      if (weeklyMoment.isBefore(this.viewEndDate())) {
        const weeksMonth = weeklyMoment.isoWeek();
        weeks.push(weeksMonth);
      }
    }
    this.weekPerMonth.set(weeks);

    const selectedYear = this.viewBeginDate().getFullYear();
    const selectedMonth = this.viewBeginDate().getMonth();
    const selectedWeek = getWeekString(this.viewEndDate());
    const selectedDay = this.viewBeginDate().getDate();
    let queryConstraints = [];
    switch (this.view()) {
      case ViewMode.Month:
        queryConstraints = [where('year', '==', selectedYear), where('month', '==', selectedMonth)];
        break;
      case ViewMode.Week:
        queryConstraints = [where('week', '==', selectedWeek)];
        break;
      case ViewMode.Day:
      case ViewMode.List:
        queryConstraints = [
          where('year', '==', selectedYear),
          where('month', '==', selectedMonth),
          where('day', '==', selectedDay),
        ];
        break;
      default:
        queryConstraints = [where('week', '==', selectedWeek)];
        break;
    }

    if (this.planType() === PlanType.Rides || fetchAll) {
      this.fetchRideEvents([...queryConstraints]);
    }

    if (this.planType() === PlanType.Drivers || fetchAll) {
      this.fetchDaypartDayDriverEvents([...queryConstraints]);
    }

    if (this.planType() === PlanType.Planners || fetchAll) {
      this.fetchDaypartDayPlannerEvents([...queryConstraints]);
    }
  }

  fetchRideEvents(queryConstraints: any[]) {
    if (this.ridesSnapshot) {
      this.ridesSnapshot();
    }
    this.allRideEvents.set([]);
    try {
      this.ridesSnapshot = onSnapshot(
        query(
          collection(db, `organisations/${dataStore.selectedOrganisation.id}/rides`),
          ...queryConstraints,
        ),
        (snapshot) => {
          try {
            const rides: Ride[] = snapshot.docs.map((doc) => {
              const data = doc.data() as Ride;
              data.id = doc.id;
              data.start = valueToDate(data.start);
              data.end = valueToDate(data.end);
              return data;
            });

            const events = [];
            for (const ride of rides) {
              const selectedVehicle = this.vehicles().find((vehicle) => {
                return vehicle.id === ride.vehicleId;
              });

              let color = {
                primary: '#bfbfbf',
                secondary: '#FFFFFF',
              };

              if (selectedVehicle) {
                color.primary = selectedVehicle.calendarStyle.color;
                color.secondary = selectedVehicle.calendarStyle.color
                  ? selectedVehicle.calendarStyle.color
                  : '#EFEFEF';
                if (ride.exception) {
                  color = {
                    primary: '#afafaf',
                    secondary: '#afafaf',
                  };
                } else if (ride.special && dataStore.generalSettings.activateSignalColor) {
                  color = {
                    primary: dataStore.generalSettings.signalColor,
                    secondary: dataStore.generalSettings.signalColor,
                  };
                } else if (
                  this.showDriverPlanning() &&
                  !dataStore.generalSettings.ignoreDriverStatus &&
                  !ride.driverId
                ) {
                  color.secondary = selectedVehicle.calendarStyle.backgroundColorNoDriver
                    ? selectedVehicle.calendarStyle.backgroundColorNoDriver
                    : '#EFEFEF';
                } else if (ride.availableSeats === 0) {
                  color.secondary = selectedVehicle.calendarStyle.backgroundColorFull
                    ? selectedVehicle.calendarStyle.backgroundColorFull
                    : '#EFEFEF';
                } else if (ride.availableSeats === selectedVehicle.seats) {
                  color.secondary = selectedVehicle.calendarStyle.backgroundColorEmpty
                    ? selectedVehicle.calendarStyle.backgroundColorEmpty
                    : '#EFEFEF';
                }
              }

              let title = ride.title
                ? ride.title
                : ride.availableSeats === 0
                  ? 'VOL'
                  : `${ride.availableSeats} Vrij`;

              if (ride.remarkFromDriver || this.rideHasDriverRemarks(ride.users)) {
                title = '<span class="title-italic">' + title + ' </span>';
              }
              if (ride.hasCopy) {
                title = `<span class="ride-icons-wrapper"><span class="ride-icon">repeat</span>&nbsp;${title}</span>`;
              }

              const aRGBHex = color.secondary.replace('#', '').match(/.{1,2}/g);
              let RGB = 1;
              if (aRGBHex && aRGBHex.length >= 3) {
                RGB =
                  1 -
                  (0.299 * parseInt(aRGBHex[0], 16) +
                    0.733 * parseInt(aRGBHex[1], 16) +
                    0.114 * parseInt(aRGBHex[2], 16)) /
                    255;
              }

              title = '<span>' + title + '</span>';
              if (ride.end - ride.start > this.msPerSegment()) {
                title =
                  title +
                  '<span class="position-fix">' +
                  moment(new Date(ride.start)).format('HH:mm') +
                  ' - ' +
                  moment(new Date(ride.end)).format('HH:mm') +
                  '</span>';
              }

              if (ride.exception) {
                title =
                  title +
                  '<span class="position-fix"> Uitzondering: ' +
                  ride.exception.title +
                  '</span>';
              }

              let classes = '';
              if (new Date(ride.end) <= new Date()) {
                classes = 'event-done ';
              }
              if (RGB < 0.45) {
                classes = classes + 'text-color-black';
              } else {
                classes = classes + 'text-color-white';
              }
              events.push({
                title,
                start: ride.start,
                end: ride.end,
                cssClass: classes,
                color,
                meta: {
                  ride,
                  vehicle: selectedVehicle,
                },
                draggable: this.view() !== ViewMode.Month,
              });
            }
            this.allRideEvents.set(events);
            if (this.planType() === PlanType.Rides) {
              this.filterEvents();
            }
          } catch (error) {
            console.error('Error processing ride events:', error);
            this.filteredEvents$.next([]);
          }
        },
        (error) => {
          console.error('Snapshot error:', error);
          this.filteredEvents$.next([]);
        },
      );
    } catch (e) {
      console.error('Error setting up snapshot', e);
      this.filteredEvents$.next([]);
    }
  }

  fetchDaypartDayDriverEvents(queryConstraints: any[]) {
    if (this.daypartDaysDriverSnapshot) {
      this.daypartDaysDriverSnapshot();
    }
    this.allDaypartDaysDriverEvents.set([]);
    queryConstraints.push(where('type', '==', 'driver'));
    this.daypartDaysDriverSnapshot = onSnapshot(
      query(
        collection(db, `organisations/${dataStore.selectedOrganisation.id}/daypartDays`),
        ...queryConstraints,
      ),
      (snapshot) => {
        const daypartDays = snapshot.docs.map((doc) => {
          const data = doc.data() as DaypartDay;
          data.id = doc.id;
          return data;
        });

        let events = [];
        for (const vehicle of this.vehicles()) {
          for (const dayPart of this.dayPartsDriver()) {
            if (!dayPart.vehicles.includes(vehicle.id)) {
              continue;
            }
            const eventObj = this.getDaypartDayEvents(dayPart, daypartDays, { vehicle: vehicle });
            if (eventObj) {
              events = events.concat(eventObj);
            }
          }
        }

        this.allDaypartDaysDriverEvents.set(events);
        if (this.planType() === PlanType.Drivers) {
          this.filterEvents();
        }
      },
      (error) => {
        console.error('Snapshot error:', error);
        this.filteredEvents$.next([]);
      },
    );
  }

  fetchDaypartDayPlannerEvents(queryConstraints: any[]) {
    if (this.daypartDaysPlannerSnapshot) {
      this.daypartDaysPlannerSnapshot();
    }
    this.allDaypartDaysPlannerEvents.set([]);
    queryConstraints.push(where('type', '==', 'planner'));
    this.daypartDaysPlannerSnapshot = onSnapshot(
      query(
        collection(db, `organisations/${dataStore.selectedOrganisation.id}/daypartDays`),
        ...queryConstraints,
      ),
      (snapshot) => {
        const daypartDays = snapshot.docs.map((doc) => {
          const data = doc.data() as DaypartDay;
          data.id = doc.id;
          return data;
        });
        let events = [];
        for (const dayPart of this.dayPartsPlanner()) {
          const eventObj = this.getDaypartDayEvents(dayPart, daypartDays);
          if (eventObj) {
            events = events.concat(eventObj);
          }
        }
        this.allDaypartDaysPlannerEvents.set(events);
        if (this.planType() === PlanType.Planners) {
          this.filterEvents();
        }
      },
      (error) => {
        console.error('Snapshot error:', error);
        this.filteredEvents$.next([]);
      },
    );
  }

  getDaypartDayEvents(dayPart: Daypart, daypartDays: DaypartDay[], optionalData: any = null) {
    let color = optionalData?.color ?? dataStore.publicSettings.primaryColor;
    if (optionalData?.vehicle?.calendarStyle?.color) {
      color = optionalData.vehicle.calendarStyle.color;
    }
    const firstDateOfWeek = moment(this.viewBeginDate()).startOf('isoWeek');
    const start = moment(firstDateOfWeek)
      .add(dayPart.daypart.day, 'days')
      .hour(dayPart.daypart.startHour)
      .minute(dayPart.daypart.startMin);
    const end = moment(firstDateOfWeek)
      .add(dayPart.daypart.day, 'days')
      .hour(dayPart.daypart.endHour)
      .minute(dayPart.daypart.endMin);

    let endWeek = 0;
    let startWeek = 0;
    if (this.view() === ViewMode.Month) {
      const monthStart = moment(this.viewBeginDate()).startOf('month');
      const currentDate = moment(monthStart).day(dayPart.daypart.day + 1);
      while (currentDate.isSameOrBefore(monthStart.endOf('month'))) {
        endWeek++;
        if (currentDate.month() < monthStart.month()) {
          startWeek++;
        }
        currentDate.add(7, 'days');
      }
    } else {
      endWeek = 1;
    }

    let events = [];
    for (let weekIncrement = startWeek; weekIncrement < endWeek; weekIncrement++) {
      let eventObj: CalendarEvent = {
        title: `${dayPart.daypart.name}`,
        start: moment(start).add(weekIncrement, 'weeks').toDate(),
        end: moment(end).add(weekIncrement, 'weeks').toDate(),
        cssClass: 'text-color-black',
        color: {
          primary: color,
          secondary: '#FFFFFF',
        },
        meta: {
          dayPart,
          dayPartDay: null,
        },
      };
      if (optionalData?.vehicle) {
        eventObj.title = `${dayPart.daypart.name} - ${optionalData.vehicle.name}`;
        eventObj.meta.vehicle = optionalData.vehicle;
      }

      const startString = getDateString(eventObj.start);
      const dayPartDay = daypartDays.find((dayPartDay: DaypartDay) => {
        return (
          dayPartDay.startString == startString &&
          (!dayPartDay?.id || dayPartDay?.daypartId == dayPart.id) &&
          (!optionalData?.vehicle || dayPartDay?.vehicleId == optionalData?.vehicle.id)
        );
      });
      if (dayPartDay) {
        eventObj.meta.dayPartDay = dayPartDay;

        if (this.planType() === PlanType.Planners) {
          if (dayPartDay?.planners?.length > 0) {
            eventObj.title = `${eventObj.title}: <br><br> ${dayPartDay.planners.join('<br>')}`;
            eventObj.color.secondary = color;
          }
        } else if (this.planType() === PlanType.Drivers) {
          if (dayPartDay.driverName) {
            eventObj.title = `${eventObj.title} - ${dayPartDay.driverName}`;
            if (dataStore.generalSettings.coDriverEnabled && dayPartDay.coDriverName) {
              eventObj.title = `${eventObj.title} - ${dayPartDay.coDriverName}`;
            }
            if (!dayPartDay.warning) {
              eventObj.color.secondary = color;
            } else {
              eventObj.cssClass = 'text-color-warn';
            }
          }
        }
        if (dayPartDay.start) {
          eventObj.start = dayPartDay.start.toDate();
        }
        if (dayPartDay.end) {
          eventObj.end = dayPartDay.end.toDate();
        }
      }
      if (new Date(eventObj.end) <= new Date()) {
        eventObj.cssClass = eventObj.cssClass + ' event-done ';
      }
      events.push(eventObj);
    }
    return events;
  }

  async getData() {
    const daypartsDocs = await getDocs(
      query(
        collection(db, `organisations/${this.organisationId}/dayparts`),
        orderBy('daypart.day', 'asc'),
      ),
    );
    daypartsDocs.forEach((daypartDoc) => {
      const daypart = daypartDoc.data() as Daypart;
      daypart.id = daypartDoc.id;
      if (daypart.type == 'driver') {
        this.dayPartsDriver.update((current) => [...current, daypart]);
      } else if (daypart.type == 'planner') {
        this.dayPartsPlanner.update((current) => [...current, daypart]);
      }
    });

    const vehiclesData = (
      await getDocs(
        query(
          collection(db, `organisations/${this.organisationId}/vehicles`),
          orderBy('calendarStyle.sortingNumber', 'asc'),
          where('active', '==', true),
        ),
      )
    ).docs.map((vehicleDoc) => {
      const vehicle = vehicleDoc.data() as Vehicle;
      vehicle.id = vehicleDoc.id;
      return vehicle;
    });
    this.vehicles.set(vehiclesData);
    this.filteredVehicles$.next(this.vehicles());

    const vehicleGroupsData = (
      await getDocs(
        query(
          collection(db, `organisations/${this.organisationId}/vehicleGroups`),
          orderBy('sortingNumber', 'asc'),
        ),
      )
    ).docs.map((vehicleGroupDoc) => {
      const vehicleGroup = vehicleGroupDoc.data() as VehicleGroup;
      vehicleGroup.id = vehicleGroupDoc.id;
      return vehicleGroup;
    });
    this.vehicleGroups.set(vehicleGroupsData);

    const driversData = (
      await getDocs(
        query(
          collection(db, `organisations/${this.organisationId}/users/`),
          where('roles', 'array-contains', Role.Driver),
        ),
      )
    ).docs.map((driverDoc) => {
      const driver = driverDoc.data() as User;
      driver.id = driverDoc.id;
      return driver;
    });
    this.drivers.set(driversData);
  }

  dayClicked({ date, events }: { date: Date; events: Array<CalendarEvent<{ ride: Ride }>> }): void {
    if (isSameMonth(date, this.viewDate())) {
      if (
        (isSameDay(this.viewDate(), date) && this.activeDayIsOpen === true) ||
        events.length === 0
      ) {
        this.activeDayIsOpen = false;
      } else {
        this.activeDayIsOpen = true;
        this.viewDate.set(date);
      }
    }
  }

  eventClicked(
    event: CalendarEvent<{
      ride: Ride;
      dayPart?: Daypart;
      vehicle: Vehicle;
      dayPartDay?: DaypartDay;
    }>,
  ): void {
    if (this.view() === ViewMode.Month) {
      if (!(this.activeDayIsOpen && isSameDay(event.start, this.viewDate()))) {
        this.dayClicked({ date: event.start, events: [event] });
        return;
      }
    }

    if (event.meta.ride) {
      this.dialog.open(RideDetailsComponent, {
        width: '800px',
        data: { ride: { ...event.meta.ride }, vehicle: event.meta.vehicle },
        autoFocus: false,
      });
    }
    if (
      (event.meta.dayPart && this.planType() == PlanType.Drivers) ||
      (event.meta.dayPart && this.planType() == PlanType.Planners && this.allowPlannerScheduling())
    ) {
      this.dialog.open(PlanDaypartComponent, {
        width: '500px',
        data: {
          start: event.start,
          end: event.end,
          dayPart: { ...event.meta.dayPart },
          vehicle: { ...event.meta.vehicle },
          dayPartDay: { ...event.meta.dayPartDay },
          planType: this.planType(),
        },
      });
    }
  }

  async eventTimesChanged({
    type,
    event,
    newStart,
    newEnd,
  }: CalendarEventTimesChangedEvent): Promise<void> {
    const oldStart = moment(event.start);
    const oldEnd = moment(event.end);
    if (!oldStart.isSame(moment(newStart)) || !oldEnd.isSame(moment(newEnd))) {
      let ride = (
        await getDoc(doc(db, `organisations/${this.organisationId}/rides/${event.meta.ride.id}`))
      ).data() as Ride;
      if (ride?.rideVersion !== 2) {
        ride = await updateRideToV2(ride);
      }
      ride.start = newStart;
      ride.end = newEnd;
      this.dialog.open(EditRideComponent, {
        width: '800px',
        data: {
          ride: { ...ride, id: event.meta.ride.id },
          oldRideDates: { start: oldStart.toDate(), end: oldEnd.toDate() },
          dateUpdated: true,
        },
      });
    }
  }

  async setViewMode(viewMode: ViewMode, refresh = true) {
    let refreshEvents = refresh;

    if (refreshEvents && this.filteredEvents$.value !== null) {
      if (
        (viewMode === ViewMode.List || viewMode === ViewMode.Day) &&
        (this.view() === ViewMode.List || this.view() === ViewMode.Day)
      ) {
        if (moment(this.viewDate()).isSame(moment(), 'day')) {
          refreshEvents = false;
        }
      } else if (viewMode === this.view()) {
        refreshEvents = false;
      }
    }

    if (
      (viewMode === ViewMode.List || viewMode === ViewMode.Day) &&
      this.view() !== ViewMode.List &&
      this.view() !== ViewMode.Day
    ) {
      this.viewDate.set(new Date());
    }

    if (refreshEvents) {
      this.filteredEvents$.next(null);
    }

    this.view.set(viewMode);
    switch (viewMode) {
      case ViewMode.Month:
        this.calendarView.set(CalendarView.Month);
        break;
      case ViewMode.Week:
        this.calendarView.set(CalendarView.Week);
        break;
      case ViewMode.Day:
        this.calendarView.set(CalendarView.Day);
        break;
      case ViewMode.List:
        this.calendarView.set(CalendarView.Day);
        break;
    }

    void updateDoc(doc(db, `organisations/${this.organisationId}/users/${dataStore.rootUser.id}`), {
      viewType: viewMode,
    }).catch((error) => {
      console.error('Failed to persist viewType', error);
    });

    if (refreshEvents) {
      this.fetchEvents();
    }
  }

  goToToday() {
    this.filteredEvents$.next(null);
    this.viewDate.set(new Date());
    this.fetchEvents();
  }

  goToPrevious() {
    this.filteredEvents$.next(null);
    const newDate = new Date(this.viewDate());
    newDate.setDate(newDate.getDate() - 1);
    this.viewDate.set(newDate);
    this.fetchEvents();
  }

  goToNext() {
    this.filteredEvents$.next(null);
    const newDate = new Date(this.viewDate());
    newDate.setDate(newDate.getDate() + 1);
    this.viewDate.set(newDate);
    this.fetchEvents();
  }

  async setListView() {
    await this.setViewMode(ViewMode.List);
  }

  planTypeChanged(value: PlanType, viewMode?: ViewMode) {
    if (
      (value !== PlanType.Rides && this.view() === ViewMode.List) ||
      value === PlanType.Automatic
    ) {
      this.setViewMode(ViewMode.Day);
    } else if (viewMode) {
      this.setViewMode(viewMode);
    }

    let pathName: string;
    switch (value) {
      case PlanType.Drivers:
        pathName = 'dayparts-driver';
        break;
      case PlanType.Planners:
        pathName = 'dayparts-planner';
        break;
      case PlanType.Automatic:
        pathName = 'automatic';
        break;
      case PlanType.Rides:
        pathName = 'rides';
        break;
    }
    if (pathName) {
      this.router.navigate([`/plannen/${pathName}`]);
    }
  }

  updatePlanType() {
    const url = this.router.url;
    let redirect = false;
    const oldPlanType = this.planType();

    switch (true) {
      case url.includes('dayparts-driver'):
        if (this.showDriverPlanning()) {
          this.planType.set(PlanType.Drivers);
        } else {
          redirect = true;
        }
        break;
      case url.includes('dayparts-planner'):
        if (this.showPlannerPlanning()) {
          this.planType.set(PlanType.Planners);
        } else {
          redirect = true;
        }
        break;
      case url.includes('automatic'):
        if (this.showAutomaticPlanning()) {
          this.planType.set(PlanType.Automatic);
        } else {
          redirect = true;
        }
        break;
      case url.includes('rides'):
        this.planType.set(PlanType.Rides);
        break;
      default:
        redirect = true;
        break;
    }

    // Clear search value when switching to/from automatic planning
    if (oldPlanType !== this.planType()) {
      const switchingToAutomatic =
        this.planType() === PlanType.Automatic && oldPlanType !== PlanType.Automatic;
      const switchingFromAutomatic =
        oldPlanType === PlanType.Automatic && this.planType() !== PlanType.Automatic;

      if (switchingToAutomatic || switchingFromAutomatic) {
        this.filterForm.controls.search.setValue('', { emitEvent: false });
      }
    }

    if (redirect) {
      const target = this.showAutomaticPlanning() ? 'automatic' : 'rides';
      this.router.navigate([`/plannen/${target}`]);
    } else {
      this.filterForm.controls.planType.setValue(this.planType(), { emitEvent: false });
      if (
        (this.planType() !== PlanType.Rides && this.view() === ViewMode.List) ||
        this.planType() === PlanType.Automatic
      ) {
        this.setViewMode(ViewMode.Day);
      } else {
        this.fetchEvents();
      }
    }
  }

  newRide(date?: Date, vehicleId?: string, automatic?: boolean) {
    if (
      (automatic && this.planType() !== PlanType.Automatic) ||
      (!automatic && this.planType() !== PlanType.Rides)
    ) {
      return;
    }

    if (!date) {
      switch (this.view()) {
        case ViewMode.Month:
          date = new Date(
            startOfMonth(new Date(this.viewDate())).setHours(this.calendarStartHour(), 0, 0, 0),
          );
          break;
        case ViewMode.Week:
          date = new Date(
            startOfWeek(new Date(this.viewDate()), { weekStartsOn: 1 }).setHours(
              this.calendarStartHour(),
              0,
              0,
              0,
            ),
          );
          break;
        case ViewMode.Day:
        case ViewMode.List:
          date = new Date(new Date(this.viewDate()).setHours(this.calendarStartHour(), 0, 0, 0));
          break;
        default:
          date = new Date(new Date().setHours(this.calendarStartHour(), 0, 0, 0));
          break;
      }
      if (date < new Date()) {
        date = new Date(new Date().setHours(this.calendarStartHour(), 0, 0, 0));
      }
    }

    const dialogData = {
      ride: {},
      defaultVehicle: vehicleId ? vehicleId : this.filterForm.value.vehicle,
      planType: automatic ? 'automatic' : 'rides',
      dates: {
        start: date,
        end: date
          ? new Date(date.getTime() + this.rideDuration() * 60000)
          : moment(
              new Date().setHours(this.calendarStartHour(), 0 + this.rideDuration(), 0, 0),
            ).toDate(),
      },
    };

    if (automatic) {
      this.dialog.open(EditAutomaticRideComponent, {
        panelClass: ['fullscreen-dialog'],
        data: {
          ride: {
            passenger: {},
            start: dialogData.dates.start,
            end: dialogData.dates.end,
          } as any,
          rideId: doc(collection(db, `organisations/${this.organisationId}/automaticRides`)).id,
        },
      });
    } else {
      this.dialog.open(EditRideComponent, {
        width: '800px',
        data: dialogData,
      });
    }
  }

  rideHasDriverRemarks(users: Map<string, any>): boolean {
    let rideHasDriverRemarks = false;
    if (users) {
      for (const [key, value] of Object.entries(users)) {
        if (value.remarkFromDriver) {
          rideHasDriverRemarks = true;
        }
      }
    }

    return rideHasDriverRemarks;
  }

  async exportPlanning() {
    try {
      this.exporting.set(true);
      switch (this.planType()) {
        case PlanType.Rides:
          await this.exportRidesPlanning();
          break;
        case PlanType.Drivers:
          await this.exportDriversPlanning();
          break;
        case PlanType.Planners:
          await this.exportPlannersPlanning();
          break;
        default:
          this.snackBar.open('Export voor dit type wordt nog niet ondersteund.', 'X', {
            duration: 5000,
          });
          break;
      }
    } catch (e) {
      this.handleExportError(e);
    } finally {
      this.exporting.set(false);
    }
  }

  async exportPlannersPlanning() {
    let exportArray = [];
    let exportType;

    try {
      if (this.view() !== ViewMode.List) {
        exportType = this.view().toString();

        for (const event of this.allDaypartDaysPlannerEvents()) {
          const eventStart = moment(event.start);
          const eventEnd = moment(event.end);
          const dayPartDay: DaypartDay = event.meta?.dayPartDay;

          if (dayPartDay?.planners?.length > 0) {
            const pushObj: any = {};
            if (this.view() === ViewMode.Week || this.view() === ViewMode.Month) {
              pushObj['Weeknummer'] = eventStart.isoWeek();
              pushObj['Datum'] = eventStart.format('DD-MM-YYYY');
            }
            pushObj['Naam planner(s)'] = dayPartDay.planners.join(', ');
            pushObj['Begintijd'] = eventStart.format('HH:mm');
            pushObj['Eindtijd'] = eventEnd.format('HH:mm');
            pushObj['Opmerking'] = dayPartDay.comment ?? '';
            pushObj['_sortKey'] = eventStart.valueOf();

            exportArray.push(pushObj);
          }
        }
        if (exportArray.length === 0) {
          this.snackBar.open('Er is geen planning om te exporteren.', 'X', {
            duration: 5000,
          });
          return;
        }
        exportArray.sort((a, b) => a._sortKey - b._sortKey);
        exportArray.forEach((item) => delete item._sortKey);

        await exportPlanningToExcel(
          exportArray,
          exportType,
          this.viewBeginDate(),
          this.viewEndDate(),
          'Planners',
        );
      } else {
        throw 'not-supported';
      }
    } catch (e) {
      throw e;
    }
  }

  async exportDriversPlanning() {
    let exportArray = [];
    let exportType;

    try {
      if (this.view() !== ViewMode.List) {
        exportType = this.view().toString();
        for (const event of this.allDaypartDaysDriverEvents()) {
          const eventStart = moment(event.start);
          const eventEnd = moment(event.end);
          const dayPartDay: DaypartDay = event.meta?.dayPartDay;

          if (dayPartDay?.driverId || dayPartDay?.driverName) {
            const pushObj: any = {};
            if (this.view() === ViewMode.Week || this.view() === ViewMode.Month) {
              pushObj['Weeknummer'] = eventStart.isoWeek();
              pushObj['Datum'] = eventStart.format('DD-MM-YYYY');
            }
            pushObj['Naam chauffeur'] = dayPartDay.driverName ?? '';
            pushObj['Naam bijrijder'] = dayPartDay.coDriverName ?? '';
            pushObj['Begintijd'] = eventStart.format('HH:mm');
            pushObj['Eindtijd'] = eventEnd.format('HH:mm');
            pushObj['Opmerking'] = dayPartDay.comment ?? '';
            pushObj['_sortKey'] = eventStart.valueOf();

            exportArray.push(pushObj);
          }
        }
        if (exportArray.length === 0) {
          this.snackBar.open('Er is geen planning om te exporteren.', 'X', {
            duration: 5000,
          });
          return;
        }
        exportArray.sort((a, b) => a._sortKey - b._sortKey);
        exportArray.forEach((item) => delete item._sortKey);

        await exportPlanningToExcel(
          exportArray,
          exportType,
          this.viewBeginDate(),
          this.viewEndDate(),
          'Chauffeurs',
        );
      } else {
        throw 'not-supported';
      }
    } catch (e) {
      throw e;
    }
  }

  handleExportError(e: any) {
    switch (e) {
      case 'no-rides':
        this.snackBar.open('Er zijn geen gegevens om te exporteren.', 'X', {
          duration: 5000,
        });
        break;
      case 'not-supported':
        this.snackBar.open('Dit type kalender wordt nog niet ondersteund.', 'X', {
          duration: 5000,
        });
        break;
      default:
        this.snackBar.open('Er ging iets mis bij het exporteren.', 'X', {
          duration: 5000,
        });
        break;
    }
  }

  async exportRidesPlanning() {
    let exportArray = [];
    const drivers = this.drivers();
    let exportType;
    let driver;
    let addressFrom: string;
    let addressTo: string;
    let cost: number | string;
    try {
      if (this.view() === ViewMode.Day || this.view() === ViewMode.Week) {
        exportType = this.view() === ViewMode.Day ? 'day' : 'week';
        for (const ride of this.allRideEvents()) {
          const rideStart = moment(ride.meta.ride.start);
          const rideEnd = moment(ride.meta.ride.end);

          if (ride.meta?.ride?.driverId) {
            driver = drivers.find(({ id }) => id === ride.meta.ride.driverId)?.name ?? 'Onbekend';
          } else {
            driver = '';
          }

          if (ride.meta?.ride?.users) {
            const users = exportRideFixUserArray(
              Object.entries(ride.meta.ride.users),
              ride.meta.ride,
            );
            users.forEach((user) => {
              addressFrom = user.from?.address ? user.from.address : (user.from ?? '');
              addressTo = user.to?.address ? user.to.address : (user.to ?? '');
              if (user.rideProduct?.cost) {
                cost = user.rideProduct.cost;
              } else {
                if (ride.meta.ride.rideProduct?.cost) {
                  cost = ride.meta.ride.rideProduct.cost;
                } else {
                  cost = 0;
                }
              }

              const pushObjRide: any = {};

              if (this.view() === ViewMode.Week) {
                pushObjRide['Weeknummer'] = rideStart.isoWeek();
                pushObjRide['Datum'] = rideStart.format('DD-MM-YYYY');
              }
              pushObjRide['Voertuig'] = ride.meta.vehicle?.name ?? '';
              pushObjRide['Chauffeur'] = driver;
              pushObjRide['Starttijd'] = rideStart.format('HH:mm');
              pushObjRide['Eindtijd'] = rideEnd.format('HH:mm');
              const originalPickup =
                ride.meta.ride.automaticallyPlanned === true
                  ? valueToDate(user.originalUserPickUpTime)
                  : null;
              const originalDropoff =
                ride.meta.ride.automaticallyPlanned === true
                  ? valueToDate(user.originalUserDropOffTime)
                  : null;
              pushObjRide['Oorspronkelijke ophaaltijd'] = originalPickup
                ? moment(originalPickup).format('HH:mm')
                : '';
              pushObjRide['Oorspronkelijke afzettijd'] = originalDropoff
                ? moment(originalDropoff).format('HH:mm')
                : '';
              pushObjRide['Ophaaladres'] = addressFrom;
              pushObjRide['Afzetadres'] = addressTo;
              pushObjRide['Naam passagier'] = user.title ?? '';
              pushObjRide['Opmerkingen voor chauffeur'] = user.commentsForDriver ?? '';
              pushObjRide['Aantal credits'] = cost;
              pushObjRide['_sortKey'] = rideStart.valueOf();

              exportArray.push(pushObjRide);
            });
          } else {
            const pushObjRide: any = {};
            if (this.view() === ViewMode.Week) {
              pushObjRide['Weeknummer'] = rideStart.isoWeek();
              pushObjRide['Datum'] = rideStart.format('DD-MM-YYYY');
            }
            pushObjRide['Starttijd'] = rideStart.format('HH:mm');
            pushObjRide['Eindtijd'] = rideEnd.format('HH:mm');
            pushObjRide['Oorspronkelijke ophaaltijd'] = '';
            pushObjRide['Oorspronkelijke afzettijd'] = '';
            pushObjRide['Ophaaladres'] = 'N.V.T.';
            pushObjRide['Afzetadres'] = 'N.V.T.';
            pushObjRide['Naam passagier'] = 'N.V.T.';
            pushObjRide['Opmerkingen voor chauffeur'] = 'N.V.T.';
            pushObjRide['Aantal credits'] = 'N.V.T.';
            pushObjRide['_sortKey'] = rideStart.valueOf();

            exportArray.push(pushObjRide);
          }
        }
        if (exportArray.length === 0) {
          this.snackBar.open('Er is geen planning om te exporteren.', 'X', {
            duration: 5000,
          });
          return;
        }
        exportArray.sort((a, b) => a._sortKey - b._sortKey);
        exportArray.forEach((item) => delete item._sortKey);
        await exportPlanningToExcel(
          exportArray,
          exportType,
          this.viewBeginDate(),
          this.viewEndDate(),
          'Ritten',
        );
      } else {
        throw 'not-supported';
      }
    } catch (e) {
      throw e;
    }
  }

  dateChanged(value: Date) {
    this.filteredEvents$.next(null);
    const newDateValue = value;
    this.viewBeginDate.set(newDateValue);
    this.viewDate.set(newDateValue);
    this.fetchEvents();
  }

  toggleMenu(): void {
    this.minimized.set(this.minimized() === 'in' ? 'out' : 'in');
  }

  isViewDateToday(): boolean {
    return moment(this.viewDate()).isSame(moment(this.currentDate()), 'day');
  }

  isCurrentViewList(): boolean {
    return this.view() === ViewMode.List;
  }

  shouldShowExportButton(): boolean {
    return (
      (this.planType() === PlanType.Rides &&
        (this.view() === ViewMode.Day || this.view() === ViewMode.Week)) ||
      ((this.planType() === PlanType.Drivers || this.planType() === PlanType.Planners) &&
        (this.view() === ViewMode.Day ||
          this.view() === ViewMode.Week ||
          this.view() === ViewMode.Month))
    );
  }
}

async function exportPlanningToExcel(
  exportArray,
  exportType,
  beginDate,
  endDate,
  sheetName = 'Planning',
) {
  let fileName = 'planning.xlsx';
  let fileNamePrefix;
  switch (sheetName) {
    case 'Chauffeurs': {
      fileNamePrefix = `chauffeurs_`;
      break;
    }
    case 'Planners': {
      fileNamePrefix = `planners_`;
      break;
    }
    case 'Ritten': {
      fileNamePrefix = `ritten_`;
      break;
    }
  }
  switch (exportType) {
    case 'day': {
      fileName = `${fileNamePrefix}planning_${moment(beginDate).format('DD-MM-YYYY')}.xlsx`;
      break;
    }
    case 'week':
    case 'month': {
      fileName = `${fileNamePrefix}planning_${moment(beginDate).format('DD-MM-YYYY')}_${moment(
        endDate,
      ).format('DD-MM-YYYY')}.xlsx`;
      break;
    }
  }
  await exportJsonToExcel(exportArray, fileName, sheetName);
}

function exportRideFixUserArray(userEntries: any[], ride: Ride) {
  const fixedUserArray = userEntries.map((entry, originalIndex) => {
    const user = { ...entry[1] };
    const id = entry[0];
    const pickupDate = valueToDate(user.userPickupTime ?? ride.start);
    return {
      ...user,
      id,
      _originalIndex: originalIndex,
      _pickupTimestamp: moment(pickupDate).valueOf(),
      _routeOrderIndex: getUserRouteOrderIndex(id, ride.locationsOrder),
    };
  });

  fixedUserArray.sort((a, b) => {
    if (a._pickupTimestamp !== b._pickupTimestamp) {
      return a._pickupTimestamp - b._pickupTimestamp;
    }
    if (a._routeOrderIndex !== b._routeOrderIndex) {
      return a._routeOrderIndex - b._routeOrderIndex;
    }
    return a._originalIndex - b._originalIndex;
  });

  return fixedUserArray.map(({ _originalIndex, _pickupTimestamp, _routeOrderIndex, ...user }) => {
    return user;
  });
}

function getUserRouteOrderIndex(userId: string, locationsOrder?: any[]) {
  if (!locationsOrder || locationsOrder.length === 0) {
    return Number.MAX_SAFE_INTEGER;
  }

  const pickupIndex = locationsOrder.findIndex((location) => {
    return location?.userId === userId && location?.type === 'from';
  });
  if (pickupIndex !== -1) {
    return pickupIndex;
  }

  const anyLocationIndex = locationsOrder.findIndex((location) => {
    return location?.userId === userId;
  });
  return anyLocationIndex === -1 ? Number.MAX_SAFE_INTEGER : anyLocationIndex;
}
